import React from 'react';
import { WeezHeader, Button, Card, Message, RadioLabel, OptionalForm, Icon, Tooltip, ToastContext } from '@weezevent/nacre';
import PropTypes from 'prop-types';

import { useNotifications, Notification } from '../../../components/notifications/notificationsProvider';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import AutomaticForm from './automaticForm';
import moment from 'moment-timezone';
import ManualForm from './manualForm';
import SelectTypeDiscount from './selectTypeDiscount';
import Conditional, { requiredField, useForm } from '../../../utils/validators';
import Style from './discountForm.module.css';
import ValidityPeriod from './validityPeriod';
import RateModel from '../../../models/rateModel';
import RateGroup from '../../../models/rateGroup';
import RestrictRate from './restrictRate';
import DiscountCodeModel from '../../../models/discountCodeModel';
import ActivateForAllEvents from './activateForAllEvents';

import { useSegment, useTrackingPage } from '../../../components/tracking/trackingProvider';

const initialDiscount = {
    discounts: [],
    type: 'amount',
    startDate: null,
    endDate: null,
    name: '',
    numberOfUse: 1,
    quantity: 1,
    prefix: '',
    unlimited: false,
    rate: {},
    ratesToSelect: [],
    ratesToUnSelect: [],
    value: '',
    account_wide: false,
    usage: 0,
    enabled: true
};

/**
 * Create a Tree of GroupRates
 * and filter the group who have no rates
 * @param {array} data: Array of GroupRates
 * @returns
 */
const createTreeGroup = (data, id = null, link = 'parent') =>
    data
        .filter(item => item[link] === id)
        .map(item => ({ ...item, children: createTreeGroup(data, item.id) }))
        .filter(item => item.rates.length > 0 || item.children.length > 0);

/**
 * Component for creating of discount
 * @param {object} organization: organisation object to have id orga
 * @param {event} event: event object to have id event
 */
export default function DiscountForm({ organization, event, discountId }) {
    const history = useHistory();
    const { t } = useTranslation();
    const { track } = useSegment();
    const toast = React.useContext(ToastContext);
    const { addNotification } = useNotifications();

    const [rates, setRates] = React.useState([]);
    const [groupsRate, setGroupsRate] = React.useState([]);
    const [ratesAndGroups, setRatesAndGroups] = React.useState({ rates: {}, groups: {} });
    const [isManual, setIsManual] = React.useState(true);
    const [openAllAccorindon, setOpenAllAccorindon] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [openOptionalForm, setOpenOptionalForm] = React.useState(false);
    const [showToggleValidityPeriod, setShowToggleValidityPeriod] = React.useState(false);

    const validateDiscounts = React.useCallback(
        (name, discounts, form) => {
            if (discounts.length === 0) {
                return t('ticket.common.required');
            }
            const errors = discounts.reduce((acc, curr, index) => (!curr.code ? [...acc, index] : acc), []);
            return errors.length > 0 ? errors : true;
        },
        [event]
    );

    const validateDate = React.useCallback(
        (name, date, dateForm) => {
            if (!showToggleValidityPeriod) {
                return true;
            }
            if (!dateForm[name]) {
                return `ticket.rates.modalSettingsEvent.${name}Missing`;
            }
            let tmpStart = moment(dateForm.startDate) || null;
            let tmpEnd = moment(dateForm.endDate) || null;
            if (tmpStart && tmpEnd && tmpStart.isAfter(tmpEnd)) {
                return t('ticket.rates.modalSettingsEvent.startDateBeforeEndDate');
            }
            if (tmpStart.isValid() && tmpEnd.isValid() && tmpStart.format('YYYY-MM-DD HH:mm') === tmpEnd.format('YYYY-MM-DD HH:mm')) {
                return t('ticket.rates.modalSettingsEvent.sameDates');
            }
            return true;
        },
        [showToggleValidityPeriod]
    );

    const requiredObjectField = React.useCallback((name, field, objectForm) => {
        if (field && Object.keys(field).length === 0) {
            return t('ticket.common.required');
        }
        return true;
    });

    const validators = React.useMemo(() => {
        return {
            discounts: validateDiscounts,
            type: requiredField,
            startDate: validateDate,
            endDate: validateDate,
            value: {
                conditionals: {
                    type: [new Conditional('different', 'ticket.common.required', 'grid'), requiredField]
                }
            },
            rate: {
                conditionals: {
                    type: [new Conditional('equals', 'ticket.common.required', 'grid'), requiredObjectField]
                }
            }
        };
    }, [validateDiscounts, validateDate, requiredObjectField]);

    const [discountForm, setDiscount, setDiscountObject, discountErrors, dicountValidate] = useForm(initialDiscount, validators);

    const amountDiscountInvalid = React.useMemo(() => {
        // For each rate, discount can be free, but not lower than 1.
        if (!discountForm.type === 'grid') {
            return false;
        }

        let validDiscount = [];
        let discountRates = Object.values(discountForm.rate);

        discountRates?.map(r => {
            const rate = rates?.find(rt => rt.id === r.rate_id);
            const totalPrice = rate?.fees.reduce((acc, fee) => acc + parseFloat(fee.amount), parseFloat(rate.price));
            const discount = totalPrice - r.value;

            return validDiscount.push(Boolean(r.value > totalPrice || (discount < 1 && discount !== 0)));
        });
        return validDiscount.some(dc => dc);
    }, [discountForm, rates]);

    React.useEffect(() => {
        dicountValidate();
    }, [discountForm]);

    React.useEffect(() => {
        if (event) {
            let rates = RateModel.getAll({ organizationId: organization.id, eventId: event.id, query: { limit: 1000 } }, { Sync: 'True' });
            let groupsRate = RateGroup.getAll({ organizationId: organization.id, eventId: event.id }, { Sync: 'True' });
            Promise.all([rates, groupsRate]).then(([rates, groupsRate]) => {
                setRates(rates.filter(rate => !rate.distributor));
                setGroupsRate(groupsRate);
            });
        }
    }, [event, toast, organization.id]);

    React.useEffect(() => {
        let groupsAndRates = groupsRate.map(groups => {
            let ratesInGroup = rates.filter(rate => rate.group === groups.id);
            return {
                id: groups.id,
                name: groups.name ? groups.name : groups.group_template ? groups.group_template.name : '',
                parent: groups.parent,
                rates: ratesInGroup
            };
        });
        let groupsAndRatesMapped = createTreeGroup(groupsAndRates);
        let ratesMapped = rates.filter(rate => !rate.group);

        setRatesAndGroups({ rates: ratesMapped, groups: groupsAndRatesMapped });
    }, [rates, groupsRate]);

    const prefixUrl = React.useCallback(() => {
        return `/ticket/O${organization.id}/E${event.id}/communicate/discounts`;
    }, [organization.id, event]);

    React.useEffect(() => {
        if (discountId) {
            DiscountCodeModel.get(
                {
                    id: discountId,
                    organizationId: organization.id,
                    eventId: event ? event.id : null,
                    cache: false
                },
                {
                    expand: 'discount,discount.discount_rules'
                }
            ).then(resDiscount => {
                const rules = resDiscount.discount.discount_rules;
                const selectedRates = rates.filter(rate => rules.map(({ rate }) => rate).includes(rate.id)).map(rate => rate.id);

                let form = {
                    discounts: [
                        {
                            name: resDiscount.discount.name,
                            code: resDiscount.code,
                            quota: resDiscount.quota
                        }
                    ],
                    type: resDiscount.discount.discount_type,
                    startDate: resDiscount.start_datetime,
                    endDate: resDiscount.end_datetime,
                    name: '',
                    rate:
                        resDiscount.discount.discount_type === 'grid' &&
                        rules.reduce((acc, curr) => (acc = Object.assign({}, acc, { [curr.rate]: { rate_id: curr.rate, value: Number(curr.amount) } })), {}),
                    ratesToSelect: resDiscount.discount.discount_type !== 'grid' ? selectedRates : [],
                    ratesToUnSelect: [],
                    value: resDiscount.discount.discount_rules[0][resDiscount.discount.discount_type],
                    usage: resDiscount.usage,
                    account_wide: resDiscount.discount.is_account_wide,
                    enabled: resDiscount.enabled
                };

                setDiscountObject(form);
                setOpenOptionalForm(Boolean(form.startDate || form.endDate || form.ratesToSelect.length > 0 || form.account_wide));
                setShowToggleValidityPeriod(Boolean(form.startDate || form.endDate));
            });
        }
    }, [rates]);

    const saveDiscount = React.useCallback(
        (discounts, discountId) =>
            DiscountCodeModel.save(discounts, {
                organizationId: organization.id,
                eventId: event.id,
                isCreateOrUpdate: true
            })
                .then(() => {
                    track('ACTION > SAVE DISCOUNT', {
                        'Page Title': 'CREATE DISCOUNT',
                        Application: 'WeezTicket nBO',
                        'Discount quantity': discounts.length
                    });
                    toast.success(t(`ticket.discountForm.${discountId ? 'editSuccess' : 'saveSuccess'}`));
                })
                .catch(e => {
                    console.error(e);
                    if (e.response?.detail?.error_type === 'already_exists') {
                        toast.error(t(`ticket.discountForm.saveError_code_already_exist`, { code: e.response?.detail?.code }));
                    } else if (e.response instanceof Array) {
                        e.response.forEach(response_error => {
                            if (response_error?.detail?.error_type === 'already_exists') {
                                toast.error(t(`ticket.discountForm.saveError_code_already_exist`, { code: response_error?.detail?.code }));
                            }
                        });
                    } else {
                        toast.error(t(`ticket.discountForm.${discountId ? 'editError' : 'saveError'}`));
                    }
                }),
        [organization, event, t, toast]
    );

    const saveBulkDiscounts = React.useCallback(
        (discounts, isManual, quantity) =>
            DiscountCodeModel.performBulkCreate(
                organization.id,
                event.id,
                discounts.map(discount => discount.object)
            ).then(response => {
                track('ACTION > SAVE DISCOUNT', {
                    'Page Title': 'CREATE DISCOUNT',
                    Application: 'WeezTicket nBO',
                    'Discount quantity': `${isManual ? discounts.length : quantity}`
                });
                addNotification(
                    new Notification('async_actions', 'create', 'discounts', response?.response?.task_id, {
                        success: isManual ? `ticket.discountForm.saveSuccess` : `ticket.discountForm.saveSuccess_auto`,
                        error: `ticket.discountForm.saveError`
                    })
                );
                toast.info(t('ticket.manager.attendees.action.generic.toast.async'));
            }),
        [organization, event, t, toast, addNotification]
    );

    const handleSave = React.useCallback(() => {
        setIsSubmitting(true);
        let discounts;
        let rates = null;
        const id = discountId ? { id: [discountId] } : null;
        const { type, discounts: discountsForm, value, quantity, prefix, ratesToSelect, account_wide } = discountForm;

        // RATES
        if (type === 'grid') {
            rates = Object.values(discountForm.rate).map(({ rate_id: rate, value, fees }) => {
                let data = { rate, value: +value };
                if (fees) {
                    data['fees'] = fees;
                }
                return data;
            });
        } else {
            if (ratesToSelect.length) {
                rates = ratesToSelect.map(id => ({ rate: id, value: +value }));
            }
        }
        // DISCOUNTS
        if (isManual) {
            discounts = discountsForm.map(({ name, code, quota }) => {
                return new DiscountCodeModel({
                    ...id,
                    name: name || null,
                    organization: organization.id,
                    event: event.id,
                    code,
                    quota: quota || null,
                    discount_type: type,
                    value: type === 'grid' ? null : Number(value),
                    start_datetime: discountForm.startDate ? moment.tz(new Date(discountForm.startDate), 'DD/MM/YYYY HH:mm', event.timezone) : null,
                    end_datetime: discountForm.endDate ? moment.tz(new Date(discountForm.endDate), 'DD/MM/YYYY HH:mm', event.timezone) : null,
                    rates,
                    enabled: discountForm.enabled,
                    account_wide
                });
            });
        } else {
            discounts = [
                new DiscountCodeModel({
                    ...id,
                    name: discountForm.name || null,
                    organization: organization.id,
                    event: event.id,
                    quota: discountForm.unlimited ? null : discountForm.numberOfUse,
                    discount_type: type,
                    value: type === 'grid' ? null : Number(value),
                    start_datetime: discountForm.startDate ? moment.tz(new Date(discountForm.startDate), 'DD/MM/YYYY HH:mm', event.timezone) : null,
                    end_datetime: discountForm.endDate ? moment.tz(new Date(discountForm.endDate), 'DD/MM/YYYY HH:mm', event.timezone) : null,
                    rates,
                    enabled: discountForm.enabled,
                    account_wide,
                    bulk_quantity: quantity,
                    prefix: prefix || null
                })
            ];
        }
        // CREATE
        (discounts.length < 20 && isManual ? saveDiscount(discounts, discountId) : saveBulkDiscounts(discounts, isManual, quantity)).finally(() => {
            setDiscountObject(initialDiscount);
            setIsSubmitting(false);
            history.push(prefixUrl());
        });
    }, [discountForm, discountId, organization, event, initialDiscount, isManual, prefixUrl, saveDiscount, saveBulkDiscounts]);
    useTrackingPage('VIEW - CREATE DISCOUNT', { 'Page Title': 'CREATE DISCOUNT', Application: 'WeezTicket nBO' });
    return (
        <>
            <WeezHeader
                title={t(`ticket.discountForm.pageTitle.${discountId ? 'edit' : 'create'}`)}
                backLink={{
                    onClick: () => {
                        track('ACTION > BACK TO DISCOUNT HOME', { 'Page Title': 'CREATE DISCOUNT', Application: 'WeezTicket nBO' });
                        history.push(prefixUrl());
                    }
                }}
                rightComponent={[
                    <Button
                        primary
                        key="primary"
                        label={t('ticket.common.action.save')}
                        isLoading={isSubmitting}
                        onClick={handleSave}
                        disabled={Object.entries(discountErrors).length > 0 || discountForm.discounts.length > 100 || amountDiscountInvalid}
                    />
                ]}
            />
            <Card.FormManager useSteps={false}>
                <Card.FormStep
                    title={t(`ticket.discountForm.infos.title`)}
                    subtitle={t('ticket.discountForm.infos.subtitle')}
                    rightComponent={
                        <Message type="tips" title={t('ticket.common.tips')} mini>
                            <p>{t('ticket.discountForm.infos.tips')}</p>
                            <p>{t('ticket.discountForm.infos.otherTips')}</p>
                        </Message>
                    }
                >
                    <>
                        <div className={Style['radio_label_container']}>
                            <Tooltip
                                className={Style['tooltip-radio-label']}
                                margin={false}
                                position={'bottom'}
                                title={t('ticket.discountForm.tooltip_manual_creation')} // offset={[0,-15]}
                                triggerOnHover
                            >
                                <RadioLabel
                                    name="type-of-creation"
                                    className={Style['input-radio']}
                                    value="manual"
                                    inline
                                    bordered
                                    label={t('ticket.discountForm.manual_creation')}
                                    active={isManual}
                                    checked={isManual}
                                    disabled={discountId}
                                    onClick={() => {
                                        track('ACTION > SELECT CREATION TYPE', { 'Page Title': 'CREATE DISCOUNT', Application: 'WeezTicket nBO', 'Creation info': 'Manual' });
                                        setDiscountObject(
                                            {
                                                discounts: []
                                            },
                                            true
                                        );
                                        setIsManual(true);
                                    }}
                                ></RadioLabel>
                            </Tooltip>
                        </div>
                        <div className={Style['radio_label_container']}>
                            <Tooltip
                                className={Style['tooltip-radio-label']}
                                margin={false}
                                position={'bottom'}
                                title={t('ticket.discountForm.tooltip_automatic_creation')}
                                triggerOnHover
                            >
                                <RadioLabel
                                    name="type-of-creation"
                                    className={Style['input-radio']}
                                    value="automatic"
                                    inline
                                    bordered
                                    label={t('ticket.discountForm.automatic_creation')}
                                    active={!isManual}
                                    checked={!isManual}
                                    disabled={discountId}
                                    onClick={() => {
                                        track('ACTION > SELECT CREATION TYPE', { 'Page Title': 'CREATE DISCOUNT', Application: 'WeezTicket nBO', 'Creation info': 'Automatic' });
                                        setDiscountObject(
                                            {
                                                discounts: [],
                                                name: '',
                                                numberOfUse: 1,
                                                quantity: 1,
                                                prefix: '',
                                                unlimited: false
                                            },
                                            true
                                        );
                                        setIsManual(false);
                                    }}
                                />
                            </Tooltip>
                        </div>
                        {isManual ? (
                            <ManualForm onDiscounts={setDiscount} discountForm={discountForm} discountErrors={discountErrors} discountId={discountId} />
                        ) : (
                            <AutomaticForm onDiscounts={setDiscount} discountForm={discountForm} discountId={discountId} />
                        )}
                    </>
                </Card.FormStep>
                <Card.FormStep
                    className={Style['select-card']}
                    title={
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>{t(`ticket.discountForm.select_type_title`)}</span>
                            {discountForm.type === 'grid' && (
                                <span
                                    className={Style['display-rate']}
                                    onClick={() => {
                                        track('ACTION > DISPLAY ALL RATES', {
                                            'Page Title': 'CREATE DISCOUNT',
                                            Application: 'WeezTicket nBO',
                                            'Display status': openAllAccorindon ? 'Hidden' : 'Displayed'
                                        });
                                        setOpenAllAccorindon(!openAllAccorindon);
                                    }}
                                >
                                    <Icon name="eye" /> {openAllAccorindon ? t(`ticket.discountForm.hide_rates`) : t(`ticket.discountForm.display_rates`)}
                                </span>
                            )}
                        </div>
                    }
                    subtitle={t('ticket.discountForm.select_type_subTitle')}
                    rightComponent
                >
                    <SelectTypeDiscount
                        discountForm={discountForm}
                        onDiscounts={setDiscount}
                        discountErrors={discountErrors}
                        event={event}
                        ratesAndGroups={ratesAndGroups}
                        openAllAccorindon={openAllAccorindon}
                        discountId={discountId}
                    />
                </Card.FormStep>
                <OptionalForm
                    open={openOptionalForm}
                    onToggle={() => {
                        track('ACTION > DISPLAY ADVANCED OPTIONS', {
                            'Page Title': 'CREATE DISCOUNT',
                            Application: 'WeezTicket nBO',
                            'Display status': openOptionalForm ? 'Hidden' : 'Displayed'
                        });
                        setOpenOptionalForm(!openOptionalForm);
                    }}
                    labelOpen={t('ticket.discountForm.display_advanced_option')}
                    labelClosed={t('ticket.discountForm.hide_advanced_option')}
                    displayAdditional
                >
                    <Card.FormStep title={t('ticket.discountForm.validy_period_title')} rightComponent>
                        <ValidityPeriod
                            onDiscounts={setDiscount}
                            discountForm={discountForm}
                            setDiscountObject={setDiscountObject}
                            discountErrors={discountErrors}
                            event={event}
                            showToggleValidityPeriod={showToggleValidityPeriod}
                            onChangeToggleValidityPeriod={() => {
                                track('ACTION > ACTIVATE VALIDITY PERIOD', {
                                    'Page Title': 'CREATE DISCOUNT',
                                    Application: 'WeezTicket nBO',
                                    Activated: showToggleValidityPeriod ? 'False' : 'True'
                                });
                                setShowToggleValidityPeriod(!showToggleValidityPeriod);
                            }}
                        />
                    </Card.FormStep>
                    <Card.FormStep title={t('ticket.discountForm.restrict_title')} rightComponent>
                        <RestrictRate organization={organization} event={event} onDiscounts={setDiscount} discountForm={discountForm} />
                    </Card.FormStep>
                    <Card.FormStep title={t('ticket.discountForm.activate_for_all_events.title')} rightComponent>
                        <ActivateForAllEvents onDiscounts={setDiscount} discountForm={discountForm} />
                    </Card.FormStep>
                </OptionalForm>
            </Card.FormManager>
        </>
    );
}

DiscountForm.propTypes = {
    event: PropTypes.object.isRequired,
    organization: PropTypes.object.isRequired,
    discountId: PropTypes.number
};
